import React from "react";
import PropTypes from "prop-types";

import {
  requestMedia,
  initRecording,
  recorderMimeType,
  createMicMonitor,
  isAudible,
  confirmSilentTake,
  createMicCheck,
  audioTrackState,
} from "../recording-utils";
import LoadingIcon from "./common/LoadingIcon";
import { isSafari } from '../utils';

import {
  MediaPermissionsErrorType,
} from 'mic-check';

class VideoRecorder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: '',
      stream: null,
      isRecording: false,
      doneRecording: false,
      mediaRecorder: null,
      elapsedSeconds: 0,
      micPrompt: 'Checking your mic…',
      micConfirmed: false,
      takeWasSilent: false,
    }

    this.streamComp = React.createRef()
    this.recordedComp = React.createRef()
    this.recordingId = 0
    this.recordedForQuestion = null
    this.elapsedTimer = null
    this.recordedBlobs = []
    this.micMonitor = null
    this.heardAudioDuringTake = false
    this.capturingTake = false
    this.micConfirmed = false
    this.micCheck = null
  }

  setupStreaming = async () => {
    const mediaResult = await requestMedia()
    let error

    if (!mediaResult[0]) {
      const { type, name, message } = mediaResult[1]

      if (type === MediaPermissionsErrorType.SystemPermissionDenied) {
        error = 'There was a problem accessing the camera and microphone'
      } else if (type === MediaPermissionsErrorType.UserPermissionDenied) {
        error = "Uh oh! VideeBop doesn't have permission to use your microphone or camera.  Please allow access in the settings for your browser, and refresh the page."
      } else if (type === MediaPermissionsErrorType.CouldNotStartVideoSource) {
        error = "Camera is in use by another application"
      } else {
        error = message
      }

      this.setState({
        error
      })
      return
    }

    const recordingResult = await initRecording()
    if (!recordingResult[0]) {
      const errorString = recordingResult[1].toString()
      if (errorString.includes('NotAllowedError') || errorString.includes('Permission denied') || errorString.includes('not allowed by the user agent')) {
        error = `Camera & Microphone Access Needed\n\nVideeBop needs access to your camera and microphone to record your video response.\n\nHow to fix this:\n1. Look for a camera/microphone icon in your browser's address bar\n2. Click it and select "Allow" or "Always allow"\n3. Refresh this page and try again\n\nOn mobile: Check your device settings to ensure the browser has permission to use the camera and microphone.`
      } else if (errorString.includes('NotFoundError') || errorString.includes('not found')) {
        error = 'Camera or microphone not found. Please make sure they are connected and try again.'
      } else if (errorString.includes('NotReadableError') || errorString.includes('in use')) {
        error = 'Your camera or microphone is being used by another app. Please close other apps (like Zoom, Skype, or video calls) and try again.'
      } else {
        error = 'Unable to access camera or microphone. Please check your browser settings and make sure your camera/microphone are connected, then refresh the page and try again.'
      }

      this.setState({
        error
      })
      return
    }

    const stream = recordingResult[1]
    const trackState = audioTrackState(stream)
    const missingMic = !trackState.hasAudioTrack
    this.setState({
      stream,
      error: missingMic
        ? 'We could not access your microphone. Please check your browser settings and try again.'
        : '',
      micPrompt: missingMic ? 'No microphone connected' : 'Checking your mic…',
      micConfirmed: false,
    }, () => {
      if (this.streamComp.current) {
        this.streamComp.current.srcObject = stream
      }
      this.startMicMonitor(stream)
    })
  }

  handleMicLevel = (level) => {
    if (this.capturingTake && isAudible(level.rms, level)) {
      this.heardAudioDuringTake = true
    }
    if (this.micConfirmed || !this.micCheck) return

    const status = this.micCheck.update(level.rms, level)
    if (status.hide) {
      this.micConfirmed = true
      this.setState({ micConfirmed: true, micPrompt: status.label })
      return
    }
    if (status.label !== this.state.micPrompt) {
      this.setState({ micPrompt: status.label })
    }
  }

  startMicMonitor = (stream) => {
    this.stopMicMonitor()
    this.micConfirmed = false
    this.micCheck = createMicCheck()
    this.micMonitor = createMicMonitor(stream, this.handleMicLevel)
    this.micMonitor.resume()
    this.setState({ micConfirmed: false, micPrompt: 'Checking your mic…' })
  }

  stopMicMonitor = () => {
    if (this.micMonitor) {
      this.micMonitor.stop()
      this.micMonitor = null
    }
  }

  stopStreaming = () => {
    this.stopMicMonitor()
    const { stream } = this.state
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
    }
  }

  handleDataAvailable = (recordingId) => (event) => {
    if (recordingId !== this.recordingId) return
    if (event.data && event.data.size > 0) {
      this.recordedBlobs.push(event.data)
    }
  }

  getBlobsUrl = () => {
    const mimeType = isSafari() ? 'video/mp4' : 'video/webm'
    return window.URL.createObjectURL(new Blob(this.recordedBlobs, {type: mimeType}));
  }

  startElapsedTimer = () => {
    this.clearElapsedTimer()
    this.setState({ elapsedSeconds: 0 })
    this.elapsedTimer = setInterval(() => {
      this.setState((prev) => ({ elapsedSeconds: prev.elapsedSeconds + 1 }))
    }, 1000)
  }

  clearElapsedTimer = () => {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer)
      this.elapsedTimer = null
    }
  }

  formatElapsed = () => {
    const total = this.state.elapsedSeconds
    const minutes = Math.floor(total / 60)
    const seconds = String(total % 60).padStart(2, '0')
    return `${minutes}:${seconds}`
  }

  startRecording = () => {
    const { stream } = this.state
    const timeSlice = 10
    let mediaRecorder

    this.recordingId += 1
    this.recordedForQuestion = this.props.question
    this.props.onRecordingStart(this.recordedForQuestion)

    try {
      mediaRecorder = new MediaRecorder(stream, recorderMimeType())
      this.recordedBlobs = []
      this.heardAudioDuringTake = false
      this.capturingTake = true
      if (this.micMonitor) this.micMonitor.resume()
      this.setState({
        mediaRecorder,
        isRecording: true,
        doneRecording: false,
        takeWasSilent: false,
        error: ''
      })
    } catch (e) {
      this.setState({
        error: `Exception while creating MediaRecorder: ${JSON.stringify(e)}`
      })
      return;
    }

    mediaRecorder.ondataavailable = this.handleDataAvailable(this.recordingId)
    mediaRecorder.start(timeSlice)
    this.startElapsedTimer()
  }

  stopRecording = () => {
    const { mediaRecorder } = this.state
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop()
    }
    this.clearElapsedTimer()
    this.capturingTake = false
    this.stopMicMonitor()
    this.setState({
      isRecording: false,
      doneRecording: true,
      takeWasSilent: !this.heardAudioDuringTake
    }, () => {
      if (this.props.onTakeReady) this.props.onTakeReady()
      setTimeout(() => {
        if (this.recordedComp.current) {
          this.recordedComp.current.src = this.getBlobsUrl()
        }
      }, 10)
    })
  }

  discardTake = ({ keepStream = true } = {}) => {
    const { mediaRecorder, stream } = this.state

    this.recordingId += 1
    this.recordedForQuestion = null
    this.recordedBlobs = []
    this.clearElapsedTimer()

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        mediaRecorder.stop()
      } catch (e) {
        // recorder already stopped
      }
    }

    if (this.recordedComp.current) {
      this.recordedComp.current.pause()
      this.recordedComp.current.removeAttribute('src')
      this.recordedComp.current.load()
    }

    if (!keepStream) {
      this.stopStreaming()
    }

    this.heardAudioDuringTake = false
    this.capturingTake = false
    this.setState({
      isRecording: false,
      doneRecording: false,
      mediaRecorder: null,
      elapsedSeconds: 0,
      takeWasSilent: false,
      stream: keepStream ? stream : null,
    }, () => {
      if (keepStream && this.streamComp.current && stream) {
        this.streamComp.current.srcObject = stream
        this.startMicMonitor(stream)
      }
      this.props.onRecordingResult()
    })
  }

  resetRecordedVideo = () => {
    this.discardTake({ keepStream: false })
    this.setupStreaming()
  }

  deleteVideo = () => {
    this.resetRecordedVideo()
  }

  acceptVideo = () => {
    if (this.state.takeWasSilent && !confirmSilentTake()) {
      return
    }

    const mimeType = isSafari() ? 'video/mp4' : 'video/webm'
    const blob = new Blob(this.recordedBlobs, {type: mimeType})
    const question = this.recordedForQuestion || this.props.question

    this.props.onAcceptedVideo(blob, question)
    this.discardTake({ keepStream: true })
  }

  componentDidMount() {
    this.setupStreaming()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.question !== this.props.question) {
      const { isRecording, doneRecording } = this.state
      if (isRecording || doneRecording) {
        this.discardTake({ keepStream: true })
      }
    }
  }

  componentWillUnmount() {
    this.clearElapsedTimer()
    this.stopStreaming()
  }

  render() {
    const {
      stream,
      doneRecording,
      isRecording,
      error,
      micPrompt,
      micConfirmed,
      takeWasSilent,
    } = this.state
    const { pendingQuestion, question, notice } = this.props
    const uploadingThisQuestion = pendingQuestion && pendingQuestion === question

    return(
      <div className="profile-recorder__capture">
        <div className="profile-recorder__stage">
          { !stream && (
            <LoadingIcon width={80} height={80} />
          )}

          { stream && !doneRecording && (
            <video ref={this.streamComp} className="video is-live" playsInline autoPlay muted></video>
          )}
          { stream && doneRecording && (
            <video ref={this.recordedComp} className="video" playsInline autoPlay loop></video>
          )}

          { isRecording && (
            <div className="profile-recorder__status" aria-live="polite">
              <span className="profile-recorder__status-dot" aria-hidden="true"></span>
              <span>Recording</span>
              <span>{this.formatElapsed()}</span>
            </div>
          )}

          { stream && !doneRecording && !micConfirmed && (
            <div className="mic-meter" aria-live="polite">
              <span className="mic-meter__label">
                {micPrompt}
              </span>
            </div>
          )}

          { doneRecording && takeWasSilent && (
            <div className="mic-meter-warning" role="status">
              We didn't detect any microphone audio in this take.
            </div>
          )}

          { notice && (
            <div className="profile-recorder__toast" role="status">
              <p className="profile-recorder__toast-title">{notice.title}</p>
              <p className="profile-recorder__toast-copy">{notice.copy}</p>
            </div>
          )}
        </div>

        { stream && !doneRecording && (
          <div className="profile-recorder__controls">
            { !isRecording && (
              <button onClick={this.startRecording} type="button" className="btn btn-success" disabled={!!uploadingThisQuestion}>
                { uploadingThisQuestion ? 'Uploading…' : 'Start Recording' }
              </button>
            )}
            { isRecording && (
              <button onClick={this.stopRecording} type="button" className="btn btn-danger">
                Stop Recording
              </button>
            )}
          </div>
        )}

        { doneRecording && (
          <div className="profile-recorder__controls">
            <button onClick={this.deleteVideo} type="button" className="btn btn-outline-danger">Redo</button>
            <button onClick={this.acceptVideo} type="button" className="btn btn-success" disabled={!!uploadingThisQuestion}>
              { uploadingThisQuestion ? 'Uploading…' : 'Keep this take' }
            </button>
          </div>
        )}

        { error && (
          <div className="profile-recorder__alerts">
            <div className="alert alert-danger mb-0" role="alert">
              {error}
            </div>
          </div>
        )}
      </div>
    )
  }
}

VideoRecorder.propTypes = {
  question: PropTypes.string,
  pendingQuestion: PropTypes.string,
  notice: PropTypes.shape({
    title: PropTypes.string,
    copy: PropTypes.string,
  }),
  onAcceptedVideo: PropTypes.func.isRequired,
  onRecordingStart: PropTypes.func.isRequired,
  onTakeReady: PropTypes.func,
  onRecordingResult: PropTypes.func.isRequired,
}

export default VideoRecorder;
